"use client";

import { useState, useEffect } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import AppLogo from "@/components/ui/AppLogo";

const navLinks = [
  { label: "Home", href: "/homepage" },
  { label: "Quem Somos", href: "/about" },
  { label: "Ambiente", href: "/environment" },
  { label: "Modalidades", href: "/services" },
  { label: "Terapias", href: "/therapies" },
  { label: "Contato", href: "/contact" },
];

export default function Header() {
  const pathname = usePathname();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled
            ? "glass-nav" :"bg-transparent"
        }`}
      >
        <div className="max-w-8xl mx-auto px-6 md:px-10 flex items-center justify-between h-20 md:h-24">
          {/* Logo */}
          <Link href="/homepage" className="flex items-center gap-3 group">
            <AppLogo
              src="/assets/images/Logo_Studio-1772740892280.png"
              size={48}
              className="transition-transform duration-300 group-hover:scale-105"
            />
            <div className="hidden sm:block">
              <p className={`font-serif text-sm font-medium leading-tight transition-colors duration-300 ${
                scrolled ? 'text-forest' : 'text-cream'
              }`}>
                Studio Pilates
              </p>
              <p className={`text-xs tracking-widest uppercase transition-colors duration-300 ${
                scrolled ? 'text-stone' : 'text-cream/70'
              }`}>
                Claudia Deichel
              </p>
            </div>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-8">
            {navLinks?.map((link) => (
              <Link
                key={link?.href}
                href={link?.href}
                className={`nav-link text-sm font-medium tracking-wide transition-colors duration-200 ${
                  pathname === link?.href
                    ? "text-gold active"
                    : scrolled
                    ? "text-charcoal hover:text-forest"
                    : "text-cream/90 hover:text-white"
                }`}
              >
                {link?.label}
              </Link>
            ))}
          </nav>

          {/* CTA + Hamburger */}
          <div className="flex items-center gap-4">
            <a
              href="https://wa.me/5551999999999?text=Olá! Gostaria de agendar uma aula experimental."
              target="_blank"
              rel="noopener noreferrer"
              className={`hidden md:inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-xs font-semibold tracking-wide transition-all duration-300 ${
                scrolled
                  ? 'bg-forest text-cream hover:bg-forest-mid shadow-soft'
                  : 'bg-gold text-forest hover:bg-gold-light'
              }`}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
              Agendar Aula
            </a>
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              className="lg:hidden w-10 h-10 flex flex-col items-center justify-center gap-1.5"
              aria-label="Toggle menu"
            >
              <span className={`block h-px transition-all duration-300 ${
                menuOpen ? 'w-6 rotate-45 translate-y-1.5 bg-forest' : scrolled ? 'w-6 bg-forest' : 'w-6 bg-cream'
              }`} />
              <span className={`block h-px transition-all duration-300 ${
                menuOpen ? 'opacity-0 w-0' : scrolled ? 'w-4 bg-forest' : 'w-4 bg-cream'
              }`} />
              <span className={`block h-px transition-all duration-300 ${
                menuOpen ? 'w-6 -rotate-45 -translate-y-1.5 bg-forest' : scrolled ? 'w-6 bg-forest' : 'w-6 bg-cream'
              }`} />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu */}
      <div className={`mobile-menu fixed inset-0 z-40 bg-cream flex flex-col pt-28 px-8 pb-10 ${menuOpen ? "open" : "closed"}`}>
        {/* Decorative element */}
        <div className="absolute top-0 right-0 w-64 h-64 bg-sage-pale rounded-full -translate-y-1/2 translate-x-1/2 opacity-50" />
        <nav className="flex flex-col gap-0 flex-1 relative z-10">
          {navLinks?.map((link, i) => (
            <Link
              key={link?.href}
              href={link?.href}
              onClick={() => setMenuOpen(false)}
              className={`py-5 border-b border-cream-beige/60 font-serif text-3xl transition-colors duration-200 flex items-center justify-between ${
                pathname === link?.href ? "text-forest" : "text-charcoal hover:text-forest"
              }`}
              style={{ transitionDelay: `${i * 40}ms` }}
            >
              {link?.label}
              {pathname === link?.href && (
                <span className="w-1.5 h-1.5 rounded-full bg-gold" />
              )}
            </Link>
          ))}
        </nav>
        <a
          href="https://wa.me/5551999999999?text=Olá! Gostaria de agendar uma aula experimental."
          target="_blank"
          rel="noopener noreferrer"
          className="btn-hero-gold w-full mt-8 relative z-10"
        >
          Agendar Aula Experimental
        </a>
        <p className="text-center text-xs text-stone mt-4 relative z-10">Porto Alegre · 3 Unidades</p>
      </div>
    </>
  );
}